import { useCallback, useMemo, useRef, useState } from "react";
import { UseEditQuizStateProps } from "./useEditQuizState";
import { ProctoringOption, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { isEnforcedProfile, isInstituteProfile } from "@verity/proctoring-option";
import { VerityInstituteSettings } from "../../../types/common";
import { EditQuizRef } from "../../editQuiz";
import { PublishLocation } from "../../edit-quiz-modals/SaveProfileModal";

type UseProfileStateProps = Pick<
	UseEditQuizStateProps,
	| "api"
	| "enforcedProfilesOnly"
	| "quizInfo"
	| "setCurrentProfileId"
	| "currentProfileId"
	| "setUploadedProfilesMap"
	| "quizType"
>;

type HandleSaveNewProfileProps = {
	lmsCourseId?: string;
	publishLocation: PublishLocation;
	proctoringOption: Partial<ProctoringOption>;
	profileName: string;
};

/**Anything related to profiles and enforced profiles */
export default function useProfileState({
	api,
	enforcedProfilesOnly,
	quizInfo,
	setCurrentProfileId,
	currentProfileId,
	setUploadedProfilesMap,
	quizType,
}: UseProfileStateProps) {
	const {
		updateProctoringProfile,
		getProctoringProfiles,
		doesUserHaveInstituteProfilePermissions,
		createProctoringProfile,
		deleteProctoringProfile,
		getPresetProfiles,
	} = api;
	const [profiles, setProfiles] = useState<Array<ProctoringOptionProfileV2>>([]);
	const [hasInstituteProfilePermissions, setHasInstituteProfilePermissions] = useState(false);

	// for when the custom button is clicked on the profile step -> probably a more elgant way of doing this using active profile
	// cant figure out how to differentiate when the user is doing custom config (activeProfile === undefined) or when its the first time (activeProfile === undefined)
	// Need to know because expected behavior is to hide the platform/config step on first time (activeProfile === undefined)
	// BUT we have to show the platform/config if user chooses custom config (activeProfile === undefined)
	// It is also a state because need to update UI based on if they click custom button
	const [isCustomConfig, setIsCustomConfig] = useState<boolean>(false);

	// The initial state of custom config depends on activeProfile which depends on the profiles being fetched,
	// which means this need to use a useEffect to initialize.
	// Ref so it only sets once
	const initialCustomConfigSet = useRef<boolean>(false);

	const [isUpdateCurrentProfileClicked, setIsUpdateCurrentProfileClicked] = useState(false); // true when user wants to update the current profile
	const [saveProfileModalShowing, setSaveProfileModalShowing] = useState(false);

	const getProfile = useCallback(
		(profileId: number) => {
			return profiles.find((p) => p.id === profileId);
		},
		[profiles],
	);

	/**The profile of curruentProfileId */
	const activeProfile = useMemo(() => {
		const foundProfile = currentProfileId ? getProfile(currentProfileId) : undefined;
		if (foundProfile && (!enforcedProfilesOnly || isEnforcedProfile(foundProfile))) {
			return foundProfile;
		} else {
			return undefined;
		}
	}, [currentProfileId, enforcedProfilesOnly, getProfile]);

	const profileMap = useMemo(() => {
		const map = new Map<number, ProctoringOptionProfileV2>(
			profiles.map((x) => [x.id, x] as [number, ProctoringOptionProfileV2]),
		);
		return map;
	}, [profiles]);

	// In profile state instead of nav state because logic on whether to show steps depends on profiles
	const shouldShowPlatformConfigStep = useMemo(() => {
		// If the user explicitly clicked the Custom Config button
		if (isCustomConfig) return true;

		// If we have a profile selected and it's NOT a preset (it's a user's own profile)
		if (activeProfile && activeProfile.type !== "preset") return true;

		// If we have an ID but no activeProfile found in the list,
		// it means it's an 'assessment' type profile (Manual Config).
		if (currentProfileId && !activeProfile) return true;

		return false;
	}, [activeProfile, currentProfileId, isCustomConfig]);

	/**Are there any institution, course or user profiles? */
	const hasCreatedProfiles = useMemo(() => {
		return profiles.some((p) => p.type === "institute" || p.type === "course" || p.type === "user");
	}, [profiles]);

	/**Technically should never happen. This could happen if all the platforms are disabled */
	const hasPresets = useMemo(() => {
		return profiles.some((p) => p.type === "preset");
	}, [profiles]);

	// ONLY show Profile Step if presets exist or user has created profiles or it is enforced profiles.
	// Profile step would not show if: no presets and no profiles created
	const canShowProfileStep = useMemo(
		() => hasCreatedProfiles || hasPresets || enforcedProfilesOnly,
		[enforcedProfilesOnly, hasCreatedProfiles, hasPresets],
	);

	// Show the preset UI on the profile step if there are presets and the user has never created profiles
	const showPresetGridUI = useMemo(() => {
		return hasPresets && !hasCreatedProfiles;
	}, [hasCreatedProfiles, hasPresets]);

	const fetchAndFilterProfiles = useCallback(
		async (verityInstituteSettings: VerityInstituteSettings): Promise<Map<number, ProctoringOptionProfileV2>> => {
			const [profilesResponse, presetProfilesResponse] = await Promise.all([
				getProctoringProfiles({ lmsProvidedCourseId: quizInfo.courseId }),
				getPresetProfiles(),
			]);

			const combinedProfiles = [...profilesResponse, ...presetProfilesResponse];

			const filteredProfiles = combinedProfiles.filter(
				(profile) =>
					(!enforcedProfilesOnly || profile.instituteProfile?.enforced) &&
					((profile.proctoringOption.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
						(profile.proctoringOption.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
						(profile.proctoringOption.isMobile && verityInstituteSettings.allowMobilePlatform)),
			);

			const tempProfileMap = new Map(filteredProfiles.map((x) => [x.id, x] as [number, ProctoringOptionProfileV2]));

			setUploadedProfilesMap(tempProfileMap);
			setProfiles(filteredProfiles);

			return tempProfileMap;
		},
		[
			getProctoringProfiles,
			quizInfo.courseId,
			getPresetProfiles,
			quizType,
			setUploadedProfilesMap,
			enforcedProfilesOnly,
		],
	);

	const fetchInstituteProfilePermissions = useCallback(async () => {
		setHasInstituteProfilePermissions(await doesUserHaveInstituteProfilePermissions());
	}, [doesUserHaveInstituteProfilePermissions]);

	const loadProfile = useCallback(
		(profileId: number | undefined) => {
			const profile = profileId ? getProfile(profileId) : undefined;
			setCurrentProfileId(profileId);
			setIsCustomConfig(!profile); // No profile means custom config

			return profile;
		},
		[getProfile, setCurrentProfileId],
	);

	const saveNewProfile = useCallback(
		async ({ lmsCourseId, publishLocation, proctoringOption, profileName }: HandleSaveNewProfileProps) => {
			const isCourseScoped = publishLocation === "course";

			const newProfile = await createProctoringProfile({
				proctoringOption,
				isInstituteProfile: publishLocation === "institute",
				lmsProvidedCourseId: isCourseScoped ? lmsCourseId : undefined,
				name: profileName,
			});

			setCurrentProfileId(newProfile.id);

			return newProfile;
		},
		[createProctoringProfile, setCurrentProfileId],
	);

	const updateProfile = useCallback(
		async (id: number, proctoringOption: Partial<ProctoringOption>, name: string | undefined) => {
			setIsUpdateCurrentProfileClicked(true);

			return await updateProctoringProfile({
				id,
				proctoringOption,
				name: name?.trim(),
			});
		},
		[updateProctoringProfile],
	);

	const deleteProfile = useCallback(
		async (profile: ProctoringOptionProfileV2) => {
			await deleteProctoringProfile(profile.id);

			if (profile.id === currentProfileId) {
				setCurrentProfileId(undefined);
			}
		},
		[currentProfileId, deleteProctoringProfile, setCurrentProfileId],
	);

	const isDeleteProfileAllowed = useCallback(
		(profile: ProctoringOptionProfileV2) => {
			return (
				(!isInstituteProfile(profile) || hasInstituteProfilePermissions) &&
				!isEnforcedProfile(profile) &&
				profile.type !== "preset"
			);
		},
		[hasInstituteProfilePermissions],
	);

	const initCustomConfigState = useCallback(() => {
		if (initialCustomConfigSet.current) return;
		// If we have currentProfileId but activeProfile is undefined, they are re-editing a quiz which doesnt have a profile
		const isCustomWorkflow = currentProfileId !== undefined && !activeProfile;

		initialCustomConfigSet.current = true;
		setIsCustomConfig(isCustomWorkflow);
	}, [activeProfile, currentProfileId]);

	const saveAsProfileButtonHandler: EditQuizRef["saveAsProfileButtonHandler"] = useCallback(() => {
		setSaveProfileModalShowing(true);
	}, []);

	const hideSaveProfileModal = useCallback(() => setSaveProfileModalShowing(false), []);

	return {
		state: {
			isUpdateCurrentProfileClicked,
			profiles,
			hasInstituteProfilePermissions,
			profileMap,
			activeProfile,
			isCustomConfig,
			shouldShowPlatformConfigStep,
			canShowProfileStep,
			showPresetGridUI,
			saveProfileModalShowing,
		},
		action: {
			getProfile,
			updateProfile,
			fetchAndFilterProfiles,
			fetchInstituteProfilePermissions,
			loadProfile,
			saveNewProfile,
			deleteProfile,
			isDeleteProfileAllowed,
			initCustomConfigState,
			saveAsProfileButtonHandler,
			hideSaveProfileModal,
		},
	};
}
